import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { callWithAuth, getLessonData } from "@/api/Api";
import {
  GetLessonDataRequest,
  GetLessonDataResponse,
  UserVideoWord,
} from "@shared_types";
import { LESSON_DATA_QUERY_KEY } from "@/query_keys";

export const useLessonData = (lessonId: number) => {
  const navigate = useNavigate();
  const queryKey = [LESSON_DATA_QUERY_KEY + lessonId];
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey,
    // if the device is offline, don't refetch
    // when it reconnects retrying requests should pass.
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    enabled: lessonId > 0, // Only run the query if we have a valid lessonId
    retry: (failureCount, error) => {
      if (error.message.includes("please create an account")) {
        navigate(`/signup`);
        return false; // Do not retry on 403
      }
      if (error.message.includes("please upgrade")) {
        navigate(`/upgrade`);
        return false; // Do not retry on 403
      }
      return failureCount < 3; // Retry up to 3 times for other errors
    },
    queryFn: () => {
      const data: GetLessonDataRequest = {
        lessonId,
      };
      return callWithAuth(getLessonData, data);
    },
  });

  return {
    query,
    updateLocalLessonData: (ds: UserVideoWord[]) => {
      queryClient.setQueryData(
        [LESSON_DATA_QUERY_KEY + lessonId],
        (oldData: GetLessonDataResponse) => {
          const newData: GetLessonDataResponse = {
            ...oldData,
            video: { ...oldData.video, userVideoWords: ds },
          };
          return newData;
        }
      );
    },
  };
};
